import { loading, rightArrow, search } from "@simplybusiness/icons";
import { Button, Icon, Link, TextField } from "@simplybusiness/mobius";
import {
  type TradeOption,
  type TradeSelectionMeta,
  type TradeSelectorProps,
  useFetchTrades,
} from "@simplybusiness/mobius-trade-selector";
import { useSnowplowContext } from "@simplybusiness/services";
import { useEffect, useId, useRef, useState } from "react";
import { useEnvironmentValues } from "../../../../../../../../hooks/environment";
import { HelpLink } from "../../../../HelpLink";
import { QuestionLabel } from "../../../../QuestionLabel";
import type { QuestionComponentProps } from "../../../types";
import { redirect } from "../redirect";
import {
  convertTradesToOptions,
  EMPTY_TRADE_OPTION,
  getTradeOptionFromAnswer,
} from "../utils";
import { AiTradeQuestionSelected } from "./AiTradeQuestionSelected";
import { TradesResults } from "./index";

export const AiTradeQuestion = ({
  questionFieldProps,
  config,
}: QuestionComponentProps) => {
  const {
    onChange,
    name,
    questionLabelProps,
    helpInfo,
    errorMessage,
    value,
    placeholder,
  } = questionFieldProps;
  const {
    occupationsPageUrl,
    tradeDefinition,
    allowTradeBasedRedirect,
    ...tradeSelectorConfig
  } = config || {};
  const typedTradeSelectorConfig =
    tradeSelectorConfig as unknown as TradeSelectorProps;
  const answeredTrade = value?.toString() ?? "";

  const { site, vertical, affclie, journeyId } = useEnvironmentValues();
  const { snowplow } = useSnowplowContext();
  const isUS = site === "simplybusiness_us";
  const labelId = useId();

  const { tradesEndpoint, searchScope, primaryDetail } =
    typedTradeSelectorConfig;

  // State for the search query and selected trade
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTrade, setSelectedTrade] = useState<TradeOption>(
    getTradeOptionFromAnswer(
      answeredTrade,
      tradeDefinition as string | undefined,
    ),
  );

  // Keep selectedTrade in sync with answeredTrade
  useEffect(() => {
    if (answeredTrade && answeredTrade !== selectedTrade.value) {
      setSelectedTrade(
        getTradeOptionFromAnswer(
          answeredTrade,
          tradeDefinition as string | undefined,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answeredTrade]);

  // Fire primaryDetailSelected event once on page load (US only)
  const hasTrackedPageLoad = useRef(false);
  useEffect(() => {
    if (isUS && snowplow && !hasTrackedPageLoad.current) {
      hasTrackedPageLoad.current = true;
      snowplow.trigger("primaryDetailSelected", {
        answer: answeredTrade || "",
        selectionMethod: "genAI",
      });
    }
  }, [isUS, snowplow, answeredTrade]);

  const tradesMutation = useFetchTrades<TradeOption[]>({
    variant: "ai",
    site,
    vertical: vertical!,
    tradesEndpoint,
    searchScope,
    primaryDetail,
    transformResults: convertTradesToOptions,
  });

  const handleFindBusiness = () => {
    const trimmedSearchQuery = searchQuery.trim();
    if (trimmedSearchQuery) {
      tradesMutation.mutate(trimmedSearchQuery);
    }
  };

  const handleTradeSelect = (trade: TradeOption, meta: TradeSelectionMeta) => {
    setSelectedTrade(trade);
    onChange?.({
      target: {
        name,
        value: trade.value,
        tradeSelectionMeta: meta,
      },
    });
    redirect({
      currentVertical: vertical,
      nextVertical: trade.vertical,
      verticalsWithoutRedirect: trade.verticalsWithoutRedirect,
      affclie,
      journeyId,
      trade: trade.value,
      allowTradeBasedRedirect: Boolean(allowTradeBasedRedirect),
    });
  };

  const handleTradeChangeClick = () => {
    // Send snowplow event for changing trade
    snowplow?.trigger("tradeChanged", {
      label: questionFieldProps.id,
      property: answeredTrade,
    });
    setSelectedTrade(EMPTY_TRADE_OPTION);
  };

  return (
    <div className="mobius-ai-trade-question">
      {!selectedTrade.value ? (
        <QuestionLabel {...questionLabelProps} labelProps={{ id: labelId }} />
      ) : null}
      {!selectedTrade.value ? helpInfo && <HelpLink {...helpInfo} /> : null}

      {selectedTrade.value ? (
        <AiTradeQuestionSelected
          trade={selectedTrade}
          onEditTrade={handleTradeChangeClick}
        />
      ) : (
        <>
          <TextField
            prefixInside={<Icon icon={search} />}
            placeholder={placeholder || "Describe your business in a few words"}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                handleFindBusiness();
              }
            }}
            errorMessage={errorMessage}
            aria-labelledby={labelId}
          />

          <div className="mobius-ai-trade-question__button">
            <Button
              onClick={handleFindBusiness}
              isDisabled={!searchQuery.trim() || tradesMutation.isPending}
            >
              {tradesMutation.isPending
                ? "Searching for trades..."
                : "Find My Business"}
              {tradesMutation.isPending ? (
                <Icon icon={loading} spin />
              ) : (
                <Icon icon={rightArrow} />
              )}
            </Button>
          </div>

          {/* Render trades list */}
          <div className="mobius-ai-trade-question__results">
            <TradesResults
              mutation={tradesMutation}
              variant="ai"
              onTradeSelect={handleTradeSelect}
            />
          </div>

          {typeof occupationsPageUrl === "string" && (
            <div className="mobius-trade-question__note">
              Or choose from the{" "}
              <Link href={occupationsPageUrl}>full list of occupations</Link>
            </div>
          )}
        </>
      )}
    </div>
  );
};
